import React from "react";
import { motion } from "framer-motion";
import { Box, Container, Typography, Grid, Button } from "@mui/material";
import PsychologyAltIcon from "@mui/icons-material/PsychologyAlt";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import SensorsIcon from "@mui/icons-material/Sensors";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import { useMicrospecThemeLanguageContext } from "../../../MicrospecDynamicUI/Themes";

const ServiceCard = ({
  title,
  subtitle,
  points,
  icon,
  accent,
  serviceLabel,
  delay = 0,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.6, delay }}
    className="h-full rounded-3xl p-6 md:p-8 relative overflow-hidden"
    style={{
      background: "rgba(255,255,255,0.62)",
      border: `1px solid ${accent}30`,
      boxShadow: `0 14px 40px ${accent}1f`,
      backdropFilter: "blur(10px)",
    }}
  >
    <div
      className="absolute -top-16 -right-10 w-44 h-44 rounded-full pointer-events-none"
      style={{ background: `${accent}22`, filter: "blur(12px)" }}
    />

    <div className="flex items-center gap-3 mb-5">
      <div
        className="w-12 h-12 rounded-2xl flex items-center justify-center"
        style={{ background: `${accent}20`, color: accent }}
      >
        {icon}
      </div>
      <div>
        <Typography className="text-[#5A4A73] text-xs tracking-[0.22em] uppercase font-semibold">
          {serviceLabel}
        </Typography>
        <Typography variant="h5" className="font-bold text-[#2D1B4E]">
          {title}
        </Typography>
      </div>
    </div>

    <Typography className="text-[#6A5B83] mb-5 leading-relaxed">
      {subtitle}
    </Typography>

    <div className="space-y-3">
      {points.map((point, i) => (
        <div
          key={i}
          className="rounded-xl px-3 py-2 text-sm"
          style={{ background: `${accent}10`, color: "#2D1B4E" }}
        >
          {point}
        </div>
      ))}
    </div>
  </motion.div>
);

const BiofeedbackSection = () => {
  const { localizationProvider, dir } = useMicrospecThemeLanguageContext();

  const sectionBadge = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.biofeedback.badge",
  });
  const sectionTitle = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.biofeedback.title",
  });
  const sectionSubtitle = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.biofeedback.subtitle",
  });
  const serviceLabel = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.biofeedback.serviceLabel",
  });
  const highlightText = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.biofeedback.highlight",
  });
  const ctaText = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.biofeedback.cta",
  });

  const neuroTitle = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.biofeedback.neuro.title",
  });
  const neuroSubtitle = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.biofeedback.neuro.subtitle",
  });
  const neuroPoints = [0, 1, 2].map((i) =>
    localizationProvider.translateByType({
      type: "Localized",
      text: `landing.biofeedback.neuro.points.${i}`,
    }),
  );

  const bioTitle = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.biofeedback.bio.title",
  });
  const bioSubtitle = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.biofeedback.bio.subtitle",
  });
  const bioPoints = [0, 1, 2].map((i) =>
    localizationProvider.translateByType({
      type: "Localized",
      text: `landing.biofeedback.bio.points.${i}`,
    }),
  );

  return (
    <Box
      id="services"
      className="relative py-24 md:py-28 overflow-hidden"
      sx={{
        backgroundImage: "url(/static/images/balls-cube.png)",
        // add opacity only to the image, not the content
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          //   backgroundImage:
          //     "linear-gradient(135deg, rgba(183, 161, 236, 0.23), rgba(115, 147, 179, 0.29))",
          zIndex: 0,
          opacity: 0.3,
        },

        backgroundSize: "auto",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "300% 120%",
        backgroundAttachment: "fixed",
      }}
    >
      <motion.div
        className="absolute -top-24 left-[8%] w-[320px] h-[320px] rounded-full bg-[#7393B3]/18 blur-[110px] pointer-events-none"
        animate={{ scale: [1, 1.1, 1], x: [0, 20, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute -bottom-20 right-[8%] w-[360px] h-[360px] rounded-full bg-[#8B5CF6]/15 blur-[120px] pointer-events-none"
        animate={{ scale: [1, 1.08, 1], y: [0, -20, 0] }}
        transition={{
          duration: 16,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 3,
        }}
      />

      <Container maxWidth="xl" dir={dir}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.55 }}
          className="text-center mb-12 md:mb-16 px-4"
        >
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-5 bg-white/70 border border-[#8B5CF6]/20">
            <SensorsIcon sx={{ fontSize: 18, color: "#8B5CF6" }} />
            <Typography className="text-xs md:text-sm tracking-[0.16em] uppercase font-semibold text-[#5A4A73]">
              {sectionBadge}
            </Typography>
          </div>

          <Typography
            variant="h2"
            className="text-4xl md:text-5xl font-bold tracking-tight text-[#2D1B4E] mb-4"
          >
            {sectionTitle}
          </Typography>

          <Typography className="text-lg text-[#6B5B7B] max-w-3xl mx-auto leading-relaxed">
            {sectionSubtitle}
          </Typography>
        </motion.div>

        <Grid container spacing={4} className="mb-8 md:mb-10">
          <Grid item xs={12} md={6}>
            <ServiceCard
              title={neuroTitle}
              subtitle={neuroSubtitle}
              points={neuroPoints}
              icon={<PsychologyAltIcon />}
              accent="#8B5CF6"
              serviceLabel={serviceLabel}
              delay={0.05}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <ServiceCard
              title={bioTitle}
              subtitle={bioSubtitle}
              points={bioPoints}
              icon={<FavoriteBorderIcon />}
              accent="#7393B3"
              serviceLabel={serviceLabel}
              delay={0.18}
            />
          </Grid>
        </Grid>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.45, delay: 0.25 }}
          className="rounded-2xl p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          style={{
            background:
              "linear-gradient(135deg, rgba(139,92,246,0.12), rgba(115,147,179,0.12))",
            border: "1px solid rgba(139,92,246,0.2)",
            backdropFilter: "blur(10px)",
          }}
        >
          <div className="flex items-center gap-3">
            <MonitorHeartOutlinedIcon sx={{ color: "#2D1B4E" }} />
            <Typography className="text-[#2D1B4E] font-semibold">
              {highlightText}
            </Typography>
          </div>
          <Button
            href="#contact"
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #7393B3 0%, #8B5CF6 100%)",
              borderRadius: "9999px",
              textTransform: "none",
              px: 3,
              py: 1,
              fontWeight: 700,
              boxShadow: "0 8px 24px rgba(139,92,246,0.3)",
            }}
          >
            {ctaText}
          </Button>
        </motion.div>
      </Container>
    </Box>
  );
};

export default BiofeedbackSection;
